import { useAuthStore } from '../../stores/auth.store';
import * as cognitoService from '../auth/cognito.service';
import { config } from '../config';
/**
 * Custom fetch wrapper that:
 * 1. Adds Authorization header with JWT token
 * 2. Adds X-Tenant-Id header (required by backend)
 * 3. Handles 401 responses with token refresh
 * 4. Provides consistent error handling
 *
 * @param baseUrl - The API base URL (e.g., config.api.people)
 * @param endpoint - The endpoint path (e.g., '/employees')
 * @param options - Fetch options
 */
export async function authenticatedFetch(baseUrl, endpoint, options = {}) {
    const { tokens, user } = useAuthStore.getState();
    if (!tokens || !user) {
        throw new Error('User not authenticated');
    }
    // Build headers
    const headers = new Headers(options.headers);
    // Use ID token (contains user claims like email, tenantId) not access token
    headers.set('Authorization', `Bearer ${tokens.idToken}`);
    headers.set('X-Tenant-Id', user.tenantId);
    headers.set('Content-Type', 'application/json');
    // Build full URL: baseUrl + endpoint
    const url = `${baseUrl}${endpoint}`;
    let response = await fetch(url, {
        ...options,
        headers,
    });
    // Handle 401: Token expired, try to refresh
    if (response.status === 401) {
        try {
            console.log('Access token expired, refreshing...');
            // Refresh session
            const result = await cognitoService.refreshSession();
            // Update tokens in store
            useAuthStore.getState().setUser(result.user);
            useAuthStore.getState().setTokens(result.tokens);
            // Retry request with new token (use ID token not access token)
            headers.set('Authorization', `Bearer ${result.tokens.idToken}`);
            response = await fetch(url, {
                ...options,
                headers,
            });
        }
        catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Refresh failed, logout user
            useAuthStore.getState().logout();
            // Redirect to login (handled by ProtectedRoute)
            throw new Error('Session expired. Please log in again.');
        }
    }
    return response;
}
/**
 * Create a typed API client for a specific service
 */
function createApiClient(baseUrl) {
    return {
        /**
         * GET request with authentication
         */
        async get(endpoint) {
            const response = await authenticatedFetch(baseUrl, endpoint, {
                method: 'GET',
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * POST request with authentication
         */
        async post(endpoint, data) {
            const options = {
                method: 'POST',
            };
            if (data) {
                options.body = JSON.stringify(data);
            }
            const response = await authenticatedFetch(baseUrl, endpoint, options);
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * PUT request with authentication
         */
        async put(endpoint, data) {
            const options = {
                method: 'PUT',
            };
            if (data) {
                options.body = JSON.stringify(data);
            }
            const response = await authenticatedFetch(baseUrl, endpoint, options);
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * PATCH request with authentication
         */
        async patch(endpoint, data) {
            const options = {
                method: 'PATCH',
            };
            if (data) {
                options.body = JSON.stringify(data);
            }
            const response = await authenticatedFetch(baseUrl, endpoint, options);
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * DELETE request with authentication
         */
        async delete(endpoint) {
            const response = await authenticatedFetch(baseUrl, endpoint, {
                method: 'DELETE',
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            // Handle 204 No Content
            if (response.status === 204) {
                return {};
            }
            return response.json();
        },
    };
}
/**
 * Service-specific API clients using config.api.* endpoints
 */
export const apiClient = createApiClient(config.api.people);
export const identityApiClient = createApiClient(config.api.identity);
export const timeApiClient = createApiClient(config.api.time);
export const leaveApiClient = createApiClient(config.api.leave);
export const payrollApiClient = createApiClient(config.api.payroll);
export const scheduleApiClient = createApiClient(config.api.schedule);
